/* eslint-disable waimaie/no-direct-access-localstorage */
/* jscpd:ignore-start */
import iconv from 'iconv-lite';

import { failToast, warningToast, infoToast } from '@utils/toast';
import delay from '@utils/delay';
import { getStorage } from '@utils/localStorage';
import request from '@utils/axiosWrapper';
import * as Ids from '@utils/ids';
import { isInApp, isGodMode, isUniversalWindowsXP, runtimeType } from '@utils/getEnv';
import God from '@utils/god';
import { reportCustomMetrics, CUSTOM_METRICS } from '@utils/owlReporter';
import {
    reportOrderFingerprint,
    PC_ORDER_FINGERPRINT_ID,
} from '@utils/report/orderFingerprintReport';
import { getGrayStateByToken, GRAY_AB_TOKEN } from '@utils/grayAB';
import { WmOrderType } from '../../projects/Business/pages/OrderManage/phfOrder/types';
import {
    OrderUnifiedInfo,
    OrderBusinessType,
} from '../../projects/Business/pages/OrderManage/shared/types';
import * as PrinterUtils from './printer';
import * as API from './api';
import * as parser from './templateParser';
import * as chromeUsb from './chromeUsbWrapper';

/** 餐饮新形态灰度 */
const UNIFIED_ORDER_GRAY = getGrayStateByToken(GRAY_AB_TOKEN.UNIFIED_ORDER);
const appInfo = window.wmSystemApi && window.wmSystemApi.getAppInfo();

if (!appInfo || appInfo.code < 4000 || !window.wmPrinterApi) {
    console.error('请在新壳子中使用此模块');
}

/** 支持打印条形码的最低客户端版本 (4.0.1.8) */
const barCodeEnabledAppCode = 4018;
/** 支持打印二维码的最低客户端版本 (4.10.0)*/
const qrCodeEnabledAppCode = 4034;

/** 当前客户端版本 */
const currentAppCode = (appInfo && appInfo.code) || 0;

/** 打印模板配置 */
type ReceiptPrintConfig = {
    1: any;
    2: any;
    3: any;
    4: any;
};

/** 广告二维码打印内容的本地存储KEY，每次外层页面刷新都会清空该KEY，每次刷新页面后第一次打印重新从接口拉取最新信息 */
const AD_CONTENT_STORAGE_KEY = 'waimai_e_print_ad_info';

/** 小票打印配置的本地存储KEY，每次外层页面刷新都会清空该KEY，每次刷新页面后第一次打印重新从接口拉取最新信息 */
const PRINT_CONFIG_STORAGE_KEY = 'waimai_e_print_config';

/** 标签打印机启用状态存储KEY */
const TAG_PRINTER_STATUS_STORAGE_KEY = 'local_tag_printer_status';

/** 门店ID与标签打印模板ID的映射关系列表存储KEY */
const POI_TAG_PRINT_TEMPLATE_ID_STORAGE_KEY = 'tagPoiPrintTemplates';

/** 失败的打印任务ID列表 */
const failedPrintTaskIds: string[] = [];

/** 加载订单所属门店的广告二维码打印内容 */
const getAdPrintContent = async (orderPoiId: number): Promise<API.AdPrintContent | null> => {
    let adPrintInfo: { [index: number]: API.AdPrintContent } = {};
    // 优先尝试加载本地存储中的配置
    try {
        adPrintInfo = JSON.parse(localStorage.getItem(AD_CONTENT_STORAGE_KEY) || '') || {};
    } catch (e) {
        adPrintInfo = {};
    }

    if (adPrintInfo[orderPoiId]) {
        return adPrintInfo[orderPoiId];
    } else {
        // 本地存储未找到，则从接口拉取
        try {
            const response = await API.getAdPrintContent(orderPoiId);
            reportCustomMetrics(CUSTOM_METRICS.PRINT_AD_API.KEY, 1, {
                [CUSTOM_METRICS.PRINT_AD_API.TAGS.HTTP_SUCCESS]: 1,
                [CUSTOM_METRICS.PRINT_AD_API.TAGS.BUSINESS_CODE]: response.code,
            });

            if (response.code === 0) {
                localStorage.setItem(
                    AD_CONTENT_STORAGE_KEY,
                    JSON.stringify({ ...adPrintInfo, [orderPoiId]: response.data }),
                );
                return response.data;
            }
            return null;
        } catch (e) {
            reportCustomMetrics(CUSTOM_METRICS.PRINT_AD_API.KEY, 1, {
                [CUSTOM_METRICS.PRINT_AD_API.TAGS.HTTP_SUCCESS]: 0,
            });
            return null;
        }
    }
};

/** 获取订单所属门店的小票打印配置信息 */
const getReceiptPrintConfig = async (orderPoiId: number): Promise<ReceiptPrintConfig | null> => {
    let allPoiPrintConfig: { [index: number]: ReceiptPrintConfig } = {};
    // 优先尝试加载本地存储中的配置
    try {
        allPoiPrintConfig = JSON.parse(localStorage.getItem(PRINT_CONFIG_STORAGE_KEY) || '') || {};
    } catch (e) {
        allPoiPrintConfig = {};
    }

    if (allPoiPrintConfig[orderPoiId]) {
        return allPoiPrintConfig[orderPoiId];
    } else {
        // 本地存储未找到，则从接口拉取
        try {
            const response = await API.getReceiptPrintConfig(orderPoiId);

            reportCustomMetrics(CUSTOM_METRICS.PRINT_TEMPLATE_CONFIG_API.KEY, 1, {
                [CUSTOM_METRICS.PRINT_TEMPLATE_CONFIG_API.TAGS.HTTP_SUCCESS]: 1,
                [CUSTOM_METRICS.PRINT_TEMPLATE_CONFIG_API.TAGS.BUSINESS_CODE]: response.code,
            });

            if (response.code === '0') {
                const config = JSON.parse(response.data);

                localStorage.setItem(
                    PRINT_CONFIG_STORAGE_KEY,
                    JSON.stringify({ ...allPoiPrintConfig, [orderPoiId]: config }),
                );
                return config;
            }

            return null;
        } catch (e) {
            reportCustomMetrics(CUSTOM_METRICS.PRINT_TEMPLATE_CONFIG_API.KEY, 1, {
                [CUSTOM_METRICS.PRINT_TEMPLATE_CONFIG_API.TAGS.HTTP_SUCCESS]: 0,
            });

            return null;
        }
    }
};

/**
 * 提取小票打印配置中的商品排序规则  返回值结构参考 商品排序参数example.json
 *
 * @param receiptPrintConfig 小票打印配置
 */
const getProductSortParams = (receiptPrintConfig: ReceiptPrintConfig) => {
    const result: { [key: string]: any } = {};
    (Object.keys(receiptPrintConfig) as Array<'1' | '2' | '3' | '4'>).forEach(key => {
        const secondData = receiptPrintConfig[key] || {};
        const secondKey = `conf-product_order_${key}`;
        const thirdData = secondData[secondKey] || {};
        if (thirdData.extend) {
            result[key] = {};
            result[key][secondKey] = {};
            result[key][secondKey].extend = thirdData.extend;
        }
    });

    return result;
};

const getTicketsCount = (
    printerSetting: PrinterUtils.ISinglePrinterSettings,
): PrinterUtils.ITicketsCount => {
    if (printerSetting.ticketsCount) {
        return printerSetting.ticketsCount;
    } else {
        if (printerSetting.orderCount) {
            return { 1: printerSetting.orderCount, 2: 0, 3: 0, 4: 0 };
        } else {
            return { 1: 1, 2: 1, 3: 1, 4: 0 };
        }
    }
};

/**
 * 为指定小票联准备数据
 *
 * @param orderPrintInfo 接口里返回原始打印信息
 * @param ticketId 要打印的小票联ID
 */
const getOrderPrintInfoForTicket = (
    orderPrintInfo: API.OrderPrintInfo,
    ticketId: '1' | '2' | '3' | '4',
    receiptPrintConfig: ReceiptPrintConfig | null,
    adPrintContent: API.AdPrintContent | null,
): API.OrderPrintInfo => {
    const basicInfo = {
        supportBarCode: currentAppCode >= barCodeEnabledAppCode ? 1 : 0, // 是否打印条形码
        receiptHoler: ticketId, // 打印的联ID
        caution: orderPrintInfo.remark, // 历史逻辑：临时兼容方案
        pickType: orderPrintInfo.pick_type, // 历史逻辑
    };

    const invoiceInfo = {
        // eslint-disable-next-line @typescript-eslint/camelcase
        invoice_title: orderPrintInfo.invoice_title,
    };

    if (
        orderPrintInfo.has_been_invoiced === 1 &&
        orderPrintInfo.invoice_title &&
        orderPrintInfo.invoice_taxpayer_id &&
        orderPrintInfo.invoice_title.indexOf('税号') === -1
    ) {
        // eslint-disable-next-line @typescript-eslint/camelcase
        orderPrintInfo.invoice_title = `${orderPrintInfo.invoice_title}（税号：${orderPrintInfo.invoice_taxpayer_id}）`;
    }

    const cartInfo = {
        cartDetailVos:
            (orderPrintInfo.cartDetailVosNew || {})[ticketId] || orderPrintInfo.cartDetailVos,
    };

    const receiptPrintConfigForTicket = receiptPrintConfig ? receiptPrintConfig[ticketId] : {};

    const adPrintContentForTicket =
        currentAppCode >= qrCodeEnabledAppCode && adPrintContent ? adPrintContent : {};

    // 合成最终数据
    return {
        ...orderPrintInfo,
        ...basicInfo,
        ...invoiceInfo,
        ...cartInfo,
        ...receiptPrintConfigForTicket,
        ...adPrintContentForTicket,
    };
};

/**
 * 负责打印订单的具体逻辑
 *
 * @param order 订单信息
 */
const performPrintAndReport = async (
    orderUnifiedInfo: OrderUnifiedInfo,
): Promise<number | null> => {
    /** 一个订单的一次打印任务ID，用于打印成功率统计去重 */
    const currentPrintTaskId = `${orderUnifiedInfo.wmOrderViewId}-${new Date().getTime()}`;

    /**
     * 订单业务类型，用于统计不同业务线的成功率
     * PHF单买、PHF拼单分开送、PHF拼单一起送均认为是PHF订单
     * 目前只有两类订单，可以做非此即彼的判断
     */
    const orderBusinessType = orderUnifiedInfo.businessType === OrderBusinessType.WM ? 'wm' : 'phf';

    /**
     * 全部打印请求计数
     * 由于【打印请求】和【打印成功/失败】分开上报，因此存在数据时移(打印计数在10:01上报，打印完成的时间为 10:02)导致的统计数据误差。
     * 在观察指标时需要考虑到这部分因素。
     */
    reportCustomMetrics(CUSTOM_METRICS.PRINT_REQUEST_ALL.KEY, 1, {
        [CUSTOM_METRICS.PRINT_REQUEST_ALL.TAGS.BUSINESS_TYPE]: orderBusinessType,
        [CUSTOM_METRICS.PRINT_REQUEST_ALL.TAGS.IS_UNIFIED]: UNIFIED_ORDER_GRAY ? 1 : 0,
    });

    const orderViewId /* A function that takes in an array of objects and returns an array of
    objects. */ =
        orderUnifiedInfo.wmOrderViewId;
    /** 订单所属门店ID */
    const orderPoiId = orderUnifiedInfo.wmPoiId;

    let adPrintContent: API.AdPrintContent | null = null;
    let receiptPrintConfig: ReceiptPrintConfig | null = null;

    // 加载广告二维码信息（低版本客户端不支持打印二维码，直接跳过）
    if (appInfo && appInfo.code >= 4034) {
        // 判断本地是否有二维码信息，如果没有则从接口拉取
        adPrintContent = await getAdPrintContent(orderPoiId);
    }
    try {
        // 加载小票打印配置
        receiptPrintConfig = await getReceiptPrintConfig(orderPoiId);
    } catch (error) {
        console.log(error);
    }

    // 获取小票商品排序规则
    let productSortParams: string | { [key: string]: any } = '';
    if (receiptPrintConfig) {
        productSortParams = getProductSortParams(receiptPrintConfig);
    }

    // 清除🖨设备信息缓存，确保每次打印使用最新
    PrinterUtils.clearSettingsCache();

    // 请求订单打印信息，入参里包含了小票排序参数，返回的信息里已经排好序
    let orderPrintInfoList: API.OrderPrintInfo[] | null = null;
    const isPhfCombineOrder = [WmOrderType.PHF_MAIN, WmOrderType.PHF_SUB].includes(
        orderUnifiedInfo.orderType || 0,
    );
    const printDetailApiMetricsConfig = isPhfCombineOrder
        ? CUSTOM_METRICS.PRINT_ORDER_DETAIL_API_PHF
        : CUSTOM_METRICS.PRINT_ORDER_DETAIL_API;
    try {
        let queryMethod = API.getOrderPrintInfo;

        // 拼好送打印走其他接口
        if (orderUnifiedInfo.businessType === OrderBusinessType.PHF) {
            // 一起送和分开送走不通接口
            if (isPhfCombineOrder) {
                queryMethod = API.getPhfCombineOrderPrintInfo;
            } else {
                queryMethod = API.getPhfSingleOrderPrintInfo;
            }
        }

        const orderPrintInfoResponse = await queryMethod({
            wmOrderViewId: orderViewId,
            wmPoiId: orderPoiId,
            getNewVo: 1,
            templateConfig: productSortParams === '' ? '' : JSON.stringify(productSortParams),
        });

        reportCustomMetrics(printDetailApiMetricsConfig.KEY, 1, {
            [printDetailApiMetricsConfig.TAGS.HTTP_SUCCESS]: 1,
            [printDetailApiMetricsConfig.TAGS.BUSINESS_TYPE]: orderBusinessType,
            [printDetailApiMetricsConfig.TAGS.BUSINESS_CODE]: orderPrintInfoResponse.code,
            [printDetailApiMetricsConfig.TAGS.IS_UNIFIED]: UNIFIED_ORDER_GRAY ? 1 : 0,
        });

        if (
            orderPrintInfoResponse.code === 0 &&
            Array.isArray(orderPrintInfoResponse.data) &&
            orderPrintInfoResponse.data.length > 0
        ) {
            orderPrintInfoList = orderPrintInfoResponse.data;
        } else if (orderPrintInfoResponse.code === 999) {
            /**
             * 场景：检测订单时间大于1天【一个设备只限制一次】
             * 处理：静默
             */
        } else {
            failToast('获取小票打印详情失败，请稍后再试');
        }
    } catch (e) {
        reportCustomMetrics(printDetailApiMetricsConfig.KEY, 1, {
            [printDetailApiMetricsConfig.TAGS.HTTP_SUCCESS]: 0,
            [printDetailApiMetricsConfig.TAGS.BUSINESS_TYPE]: orderBusinessType,
        });
        failToast('获取小票打印详情失败，请稍后再试');
    }

    if (!orderPrintInfoList) {
        reportCustomMetrics(CUSTOM_METRICS.PRINT_FAIL.KEY, 1, {
            [CUSTOM_METRICS.PRINT_FAIL.TAGS.BUSINESS_TYPE]: orderBusinessType,
            [CUSTOM_METRICS.PRINT_FAIL.TAGS.ERROR_CODE]:
                CUSTOM_METRICS.PRINT_FAIL.ERROR_CODE.ORDER_DETAIL_FETCH_FAIL,
            [CUSTOM_METRICS.PRINT_FAIL.TAGS.ORDER_POI_ID]: orderPoiId,
            [CUSTOM_METRICS.PRINT_FAIL.TAGS.ORDER_VIEW_ID]: orderViewId,
            [CUSTOM_METRICS.PRINT_FAIL.TAGS.IS_UNIFIED]: UNIFIED_ORDER_GRAY ? 1 : 0,
        });
        // 指纹上报
        reportOrderFingerprint(
            orderUnifiedInfo.wmOrderViewId,
            `${orderUnifiedInfo.wmPoiId}`,
            PC_ORDER_FINGERPRINT_ID.PC_PRINT_ORDER,
            1,
            {
                errorCode: CUSTOM_METRICS.PRINT_FAIL.ERROR_CODE.ORDER_DETAIL_FETCH_FAIL,
            },
        );
        failedPrintTaskIds.push(currentPrintTaskId);
        // 没有查询到订单打印信息，提前结束流程
        return null;
    }

    const poiEnabledConnectedPrinters = PrinterUtils.getPoiEnabledConnectedPrinters(
        `${orderPoiId}`,
    );

    // 未开启任何打印机，直接提示并退出流程
    if (poiEnabledConnectedPrinters.length === 0) {
        reportCustomMetrics(CUSTOM_METRICS.PRINT_FAIL.KEY, 1, {
            [CUSTOM_METRICS.PRINT_FAIL.TAGS.BUSINESS_TYPE]: orderBusinessType,
            [CUSTOM_METRICS.PRINT_FAIL.TAGS.ERROR_CODE]:
                CUSTOM_METRICS.PRINT_FAIL.ERROR_CODE.PRINTER_LIST_EMPTY,
            [CUSTOM_METRICS.PRINT_FAIL.TAGS.ORDER_POI_ID]: orderPoiId,
            [CUSTOM_METRICS.PRINT_FAIL.TAGS.ORDER_VIEW_ID]: orderViewId,
            [CUSTOM_METRICS.PRINT_FAIL.TAGS.IS_UNIFIED]: UNIFIED_ORDER_GRAY ? 1 : 0,
        });
        reportOrderFingerprint(
            orderUnifiedInfo.wmOrderViewId,
            `${orderUnifiedInfo.wmPoiId}`,
            PC_ORDER_FINGERPRINT_ID.PC_PRINT_ORDER,
            1,
            {
                errorCode: CUSTOM_METRICS.PRINT_FAIL.ERROR_CODE.PRINTER_LIST_EMPTY,
            },
        );
        failedPrintTaskIds.push(currentPrintTaskId);
        warningToast('当前尚未安装或选择有效的打印机');
        return null;
    }

    for (let i = 0; i < poiEnabledConnectedPrinters.length; i++) {
        const printerSetting = poiEnabledConnectedPrinters[i];
        // 万事具备，终于可以开始打印了。
        const ticketsCount = getTicketsCount(printerSetting);

        infoToast('订单正在打印，请稍等');
        const pageWidth = printerSetting.pageWidth || 0;
        const charWidth = PrinterUtils.charWidthOfPageWidth(pageWidth);

        // 循环待打印订单列表
        for (let j = 0; j < orderPrintInfoList!.length; j++) {
            const orderPrintInfo = orderPrintInfoList![j];
            // 循环各联（商家、后厨、顾客、配送）
            const ticketIdList = Object.keys(ticketsCount) as Array<keyof typeof ticketsCount>;
            for (let k = 0; k < ticketIdList.length; k++) {
                const ticketId = ticketIdList[k];
                // 构造当前联的数据（不同联的商品顺序、字号都可能不一样）
                let orderPrintInfoForTicket: API.OrderPrintInfo | null = null;
                try {
                    orderPrintInfoForTicket = getOrderPrintInfoForTicket(
                        orderPrintInfo,
                        ticketId,
                        receiptPrintConfig,
                        adPrintContent,
                    );
                } catch {
                    if (failedPrintTaskIds.indexOf(currentPrintTaskId) === -1) {
                        reportCustomMetrics(CUSTOM_METRICS.PRINT_FAIL.KEY, 1, {
                            [CUSTOM_METRICS.PRINT_FAIL.TAGS.BUSINESS_TYPE]: orderBusinessType,
                            [CUSTOM_METRICS.PRINT_FAIL.TAGS.ERROR_CODE]:
                                CUSTOM_METRICS.PRINT_FAIL.ERROR_CODE.COMPOSE_PRINT_DETAIL_FAIL,
                            [CUSTOM_METRICS.PRINT_FAIL.TAGS.ORDER_POI_ID]: orderPoiId,
                            [CUSTOM_METRICS.PRINT_FAIL.TAGS.ORDER_VIEW_ID]: orderViewId,
                            [CUSTOM_METRICS.PRINT_FAIL.TAGS.IS_UNIFIED]: UNIFIED_ORDER_GRAY ? 1 : 0,
                        });
                        reportOrderFingerprint(
                            orderUnifiedInfo.wmOrderViewId,
                            `${orderUnifiedInfo.wmPoiId}`,
                            PC_ORDER_FINGERPRINT_ID.PC_PRINT_ORDER,
                            1,
                            {
                                errorCode:
                                    CUSTOM_METRICS.PRINT_FAIL.ERROR_CODE.COMPOSE_PRINT_DETAIL_FAIL,
                            },
                        );
                        failedPrintTaskIds.push(currentPrintTaskId);
                    }
                }

                if (orderPrintInfoForTicket !== null) {
                    /**
                     * 这里有一点小逻辑（2021-05-12）
                     * showHideAddressTips showPhoneHideNotPrivacy 老接口会返回 1 or undefined(也就是不返回该字段)
                     * 下沉接口只能返回 1 or 0，导致订单小票地址、电话打印两遍
                     * 前端需要将 0 替换成 undefined
                     */
                    if (orderPrintInfoForTicket.showHideAddressTips === 0) {
                        delete orderPrintInfoForTicket.showHideAddressTips;
                    }
                    if (orderPrintInfoForTicket.showPhoneHideNotPrivacy === 0) {
                        delete orderPrintInfoForTicket.showPhoneHideNotPrivacy;
                    }

                    console.log(orderPrintInfoForTicket, 'orderPrintInfoForTicket');
                    // 每一联可以选择打印多次
                    for (let printTimes = 0; printTimes < ticketsCount[ticketId]; printTimes++) {
                        // 每次打印之间间隔 1s
                        await delay(1000);

                        // 把数据和模板解析成最终要打印的XML
                        const xml = parser.jsonToXml(orderPrintInfoForTicket, orderPoiId);
                        /** DEBUG 日志开始 */
                        if (isGodMode) {
                            const poiPrintTemplates = JSON.parse(
                                localStorage.getItem('poiPrintTemplates') || 'null',
                            );
                            let templateId = 0;
                            if (poiPrintTemplates) {
                                poiPrintTemplates.forEach((pPT: any) => {
                                    if (pPT.wmPoiId === orderPoiId) {
                                        templateId = pPT.nowTemplateId;
                                    }
                                });
                            }
                            const template = (parent as any).PRINTTEMPLATES[templateId];
                            try {
                                const logRecord = {
                                    type: '小票',
                                    wmPoiId: orderPoiId,
                                    orderViewId: orderPrintInfoForTicket.wm_order_id_view_str,
                                    seq: orderPrintInfoForTicket.wm_poi_order_dayseq,
                                    wmPoiName: orderPrintInfoForTicket.poi_name,
                                    dataToTemplate: orderPrintInfoForTicket,
                                    templateId,
                                    template,
                                    finalXML: xml,
                                };
                                God.see('printTicket', logRecord);
                            } catch (e) {
                                console.error('打印日志存储失败');
                                console.log(e);
                            }
                        }
                        /** DEBUG 日志结束 */

                        if (typeof xml === 'string') {
                            if (window.wmPrinterApi) {
                                try {
                                    window.wmPrinterApi.print(printerSetting.id, xml, {
                                        charWidth,
                                    });
                                } catch (e) {
                                    if (window.Owl && window.Owl.addError) {
                                        window.Owl.addError(e);
                                    }
                                    if (failedPrintTaskIds.indexOf(currentPrintTaskId) === -1) {
                                        reportCustomMetrics(CUSTOM_METRICS.PRINT_FAIL.KEY, 1, {
                                            [CUSTOM_METRICS.PRINT_FAIL.TAGS
                                                .BUSINESS_TYPE]: orderBusinessType,
                                            [CUSTOM_METRICS.PRINT_FAIL.TAGS.ERROR_CODE]:
                                                CUSTOM_METRICS.PRINT_FAIL.ERROR_CODE
                                                    .WM_PRINTER_API_FAIL,
                                            [CUSTOM_METRICS.PRINT_FAIL.TAGS
                                                .ORDER_POI_ID]: orderPoiId,
                                            [CUSTOM_METRICS.PRINT_FAIL.TAGS
                                                .ORDER_VIEW_ID]: orderViewId,
                                            [CUSTOM_METRICS.PRINT_FAIL.TAGS
                                                .IS_UNIFIED]: UNIFIED_ORDER_GRAY ? 1 : 0,
                                        });
                                        reportOrderFingerprint(
                                            orderUnifiedInfo.wmOrderViewId,
                                            `${orderUnifiedInfo.wmPoiId}`,
                                            PC_ORDER_FINGERPRINT_ID.PC_PRINT_ORDER,
                                            1,
                                            {
                                                errorCode:
                                                    CUSTOM_METRICS.PRINT_FAIL.ERROR_CODE
                                                        .WM_PRINTER_API_FAIL,
                                            },
                                        );
                                        failedPrintTaskIds.push(currentPrintTaskId);
                                    }
                                }
                            }
                        } else {
                            if (failedPrintTaskIds.indexOf(currentPrintTaskId) === -1) {
                                reportCustomMetrics(CUSTOM_METRICS.PRINT_FAIL.KEY, 1, {
                                    [CUSTOM_METRICS.PRINT_FAIL.TAGS
                                        .BUSINESS_TYPE]: orderBusinessType,
                                    ...xml,
                                    [CUSTOM_METRICS.PRINT_FAIL.TAGS.ORDER_POI_ID]: orderPoiId,
                                    [CUSTOM_METRICS.PRINT_FAIL.TAGS.ORDER_VIEW_ID]: orderViewId,
                                    [CUSTOM_METRICS.PRINT_FAIL.TAGS.IS_UNIFIED]: UNIFIED_ORDER_GRAY
                                        ? 1
                                        : 0,
                                });
                                reportOrderFingerprint(
                                    orderUnifiedInfo.wmOrderViewId,
                                    `${orderUnifiedInfo.wmPoiId}`,
                                    PC_ORDER_FINGERPRINT_ID.PC_PRINT_ORDER,
                                    1,
                                    {
                                        errorCode: xml.errorCode,
                                    },
                                );
                                failedPrintTaskIds.push(currentPrintTaskId);
                            }
                        }
                    }
                }
            }
        }
    }

    // 没有失败记录，上报成功！
    if (failedPrintTaskIds.indexOf(currentPrintTaskId) === -1) {
        reportCustomMetrics(
            CUSTOM_METRICS.PRINT_SUCCESS.KEY,
            1,
            {
                [CUSTOM_METRICS.PRINT_SUCCESS.TAGS.BUSINESS_TYPE]: orderBusinessType,
                [CUSTOM_METRICS.PRINT_SUCCESS.TAGS.RUNTIME]: runtimeType,
                [CUSTOM_METRICS.PRINT_SUCCESS.TAGS.IS_XP]: isUniversalWindowsXP ? 1 : 0,
                [CUSTOM_METRICS.PRINT_SUCCESS.TAGS.IS_UNIFIED]: UNIFIED_ORDER_GRAY ? 1 : 0,
            },
            {
                acctId: Ids.getAcctId(),
                wmPoiId: Ids.getWmPoiId(),
            },
        );
        reportOrderFingerprint(
            orderUnifiedInfo.wmOrderViewId,
            `${orderUnifiedInfo.wmPoiId}`,
            PC_ORDER_FINGERPRINT_ID.PC_PRINT_ORDER,
            0,
        );
    }

    // 上报打印并获取最新的打印次数
    try {
        const latestPrintCount = await API.reportOrderPrint({
            orderViewId,
            wmPoiId: orderPoiId,
            type: 0, // 小票打印
        });
        return latestPrintCount;
    } catch (e) {
        return null;
    }
};

/**
 * 点击 ”打印订单“ 时打印订单
 *
 * 对应原 printorder.js 中的 printNew 方法（对外暴露为 printOrderNew)
 *
 * 1. 上报
 * 2. 打印
 * 3. 上报打印计数
 * 3. 返回打印计数
 */
const printOrder = (orderUnifiedInfo: OrderUnifiedInfo) => {
    try {
        request.post('/api/order/today/history/print');
    } catch (error) {
        console.log(error);
    }
    return performPrintAndReport(orderUnifiedInfo);
};

/**
 * 点击 ”接单并打印“ 时打印订单
 *
 * 对应原 printorder.js 中的 printWhenConfirm 方法
 *
 * 1. 打印
 * 2. 上报打印计数
 */
const printOrderWhenConfirm = (orderUnifiedInfo: OrderUnifiedInfo) => {
    return performPrintAndReport(orderUnifiedInfo);
};

const getSimplifiedText = (key: string, value: string) => {
    // 顾客姓名 customerName（最多8字）
    // 菜品名称 foodName（最多17字）
    // 餐厅名称 poiName（最多12字）
    // 收餐地址 customerAddr（最多12字）超长处理
    // 超长保留前n-2个字 + 省略号 + 最后一个字
    let result = value;
    if (key === 'customerName' && value.length > 8) {
        result = `${value.substr(0, 6)}…${value.substr(-1)}`;
    }
    if (key === 'foodName' && value.length > 17) {
        result = `${value.substr(0, 10)}…${value.substr(-5)}`;
    }
    if (key === 'poiName' && value.length > 12) {
        result = `${value.substr(0, 7)}…${value.substr(-4)}`;
    }
    if (key === 'customerAddr' && value.length > 12) {
        result = `${value.substr(0, 7)}…${value.substr(-4)}`;
    }
    return result;
};

/**
 * 使用标签数据解析标签打印模板获得最终的打印指令
 */
const parseTagTemplate = (
    tagContent: API.OrderFoodTagPrintContent,
    poiTagTemplate: string,
): string => {
    let result = poiTagTemplate;

    Object.keys(tagContent).forEach(key => {
        const text = getSimplifiedText(key, tagContent[key]);
        result = result.replace(`#{${key}}`, text);
    });

    return `${result}\n`;
};

/**
 * 读取标签打印模板
 *
 * @param wmPoiId 门店ID
 */
const getPoiTagTemplate = (wmPoiId: number): string => {
    // 首先找到对应门店的小票打印模板
    let poiTagTemplate = '';
    const poiTagPrintTemplateIdMapList: Array<{
        version: number;
        wmPoiId: number;
        nowTemplateId: number;
    }> = JSON.parse(localStorage.getItem(POI_TAG_PRINT_TEMPLATE_ID_STORAGE_KEY) || '[]') || [];

    poiTagPrintTemplateIdMapList.forEach(entry => {
        if (entry.wmPoiId === wmPoiId) {
            poiTagTemplate = (window.parent as any).TAGPRINTTEMPLATES[
                entry.nowTemplateId
            ] as string;
        }
    });
    if (!poiTagTemplate) {
        poiTagTemplate = ((window.parent as any).TAGPRINTTEMPLATES.keep || '') as string;
    }

    return poiTagTemplate;
};

/** 标签打印模板分隔符（分隔符前面的是取餐码模板，分隔符后边的是存餐码模板） */
const POI_TAG_TEMPLATE_DELIMITER = 'TUANCAN_JOIN_SAVE_MEAL_SYMBOL';

/** 读取取餐码模板 */
const getFetchMealCodeTemplate = (wmPoiId: number): string => {
    const poiTagTemplate = getPoiTagTemplate(wmPoiId);
    return poiTagTemplate.split(POI_TAG_TEMPLATE_DELIMITER)[0] || '';
};

/** 读取存餐码模板 */
const getDepositMealCodeTemplate = (wmPoiId: number): string => {
    const poiTagTemplate = getPoiTagTemplate(wmPoiId);
    return poiTagTemplate.split(POI_TAG_TEMPLATE_DELIMITER)[1] || '';
};

/**
 * 将标签打印内容解析成可以直接发送给打印机的原始打印指令
 *
 * @param wmPoiId 门店 ID
 * @param tagContentList 需要打印的标签内容列表
 */
const getRawTagPrintContent = (
    wmPoiId: number,
    tagContentList: API.OrderFoodTagPrintContent[],
): string[] => {
    const poiTagTemplate = getFetchMealCodeTemplate(wmPoiId);
    const result: string[] = [];
    if (poiTagTemplate) {
        tagContentList.forEach(tagContent => {
            result.push(
                parseTagTemplate(
                    {
                        takeFoodNum: tagContent.takeFoodCode,
                        serialNum: tagContent.identCode,
                        customerPhone: tagContent.recipientPhone,
                        customerName: tagContent.recipientName,
                        foodName: tagContent.foodName,
                        poiName: tagContent.poiName,
                        customerAddr: tagContent.recipientAddress,
                        deliveryTime: tagContent.estimatedDeliveryTime,
                    },
                    poiTagTemplate,
                ),
            );
        });
    }

    return result;
};

const printTagsThroughChromUsb = async (device: any, content: string) => {
    const connectionHanle = await chromeUsb.openDevice(device);
    await chromeUsb.claimInterface(connectionHanle, 0);
    await chromeUsb.bulkTransfer(connectionHanle, {
        data: iconv.encode(content, 'GBK').buffer,
        direction: 'out',
        endpoint: 2,
    });
    await chromeUsb.closeDevice(connectionHanle);
};

/**
 * 打印原始的标签指令
 *
 * @param rawPrintContentList 原始标签指令数组
 */
const printRawTagData = async (rawPrintContentList: string[]) => {
    if (!isInApp) {
        warningToast('请在PC客户端中进行此操作');
        return null;
    }

    if (rawPrintContentList.length === 0) {
        return 0;
    }

    // 加载标签打印机开启状态
    const tagPrinterEnableStatus: { [index: string]: boolean } = JSON.parse(
        getStorage(TAG_PRINTER_STATUS_STORAGE_KEY) || '{}',
    );

    // 接下来找到第一台开启的标签打印机
    const allSystemPrinters = PrinterUtils.getAllPrinters();
    // 通过壳子打印 API 找到的第一台开启的标签打印机
    const enabledSystemTagPrinter = allSystemPrinters.filter(p => tagPrinterEnableStatus[p.id])[0];

    const allChromeUsbPrinters = await chromeUsb.getDevices();
    // 通过 chrome.usb 找到的第一台开启的标签打印机
    const enabledChromeUsbTagPrinter = allChromeUsbPrinters.filter(
        p => tagPrinterEnableStatus[p.productId],
    )[0];

    // 判断是否启用了标签打印机
    if (!enabledSystemTagPrinter && !enabledChromeUsbTagPrinter) {
        warningToast('暂未开启标签打印机，请在"店铺设置—系统设置—打印设置"开启');
        return null;
    }

    if (enabledSystemTagPrinter) {
        // 通过壳子批量打印API打印
        if (window.wmPrinterApi) {
            window.wmPrinterApi.batchRawPrint(enabledSystemTagPrinter.id, rawPrintContentList);
        }
    } else {
        // 通过 chrome.use API 打印
        printTagsThroughChromUsb(enabledChromeUsbTagPrinter, rawPrintContentList.join(''));
    }

    return rawPrintContentList.length;
};

/**
 * 打印订单标签(团餐订单)
 */
const printOrderTag = async (orderUnifiedInfo: OrderUnifiedInfo): Promise<null | number> => {
    // 请求要打印的标签数据
    let rawPrintContentList: string[] = [];

    try {
        const res = await API.getRawTagPrintContent({
            wmPoiId: orderUnifiedInfo.wmPoiId,
            orderViewId: orderUnifiedInfo.wmOrderViewId,
        });
        if (res.mode === 2) {
            rawPrintContentList = res.templateDataList;
        } else {
            const tagContentList = res.takeFoodCodeList;
            const depositCodeInfo = res.depositCode;

            // 解析取餐码打印指令
            rawPrintContentList = getRawTagPrintContent(orderUnifiedInfo.wmPoiId, tagContentList);

            // 解析存餐码打印指令
            const depositMealCodeTemplate = getDepositMealCodeTemplate(orderUnifiedInfo.wmPoiId);
            if (depositCodeInfo !== null && depositMealCodeTemplate !== '') {
                // 前端手动处理换行
                const fullCompanyAddr = `企业地址：${depositCodeInfo.recipientAddress}`;
                const fullCabinetInfo = `使用餐柜：${depositCodeInfo.cupboard}`;
                const depositCodePrintInfo: API.OrderFoodTagPrintContent = {
                    saveMealTime: depositCodeInfo.estimatedDeliveryTime,
                    qrCodeUrl: depositCodeInfo.depositCode,
                    companyAddr1: fullCompanyAddr.substr(0, 19),
                    companyAddr2: fullCompanyAddr.substr(19),
                    rule1: fullCabinetInfo.substr(0, 10),
                    rule2: fullCabinetInfo.substr(10, 10),
                    rule3: fullCabinetInfo.substr(20, 10),
                    rule4: fullCabinetInfo.substr(30, 10),
                    rule5: fullCabinetInfo.substr(40, 10),
                    rule6: fullCabinetInfo.substr(50, 10),
                    rule7: fullCabinetInfo.substr(60, 10),
                };

                rawPrintContentList.unshift(
                    parseTagTemplate(depositCodePrintInfo, depositMealCodeTemplate),
                );
            }
        }
    } catch (e) {
        failToast('标签数据加载失败，请稍后再试');
    }

    if (!Array.isArray(rawPrintContentList) || rawPrintContentList.length === 0) {
        return null;
    }

    const printCount = await printRawTagData(rawPrintContentList);

    if (printCount === null || printCount === 0) {
        // 没有连接的标签打印机 or 没有数据
        return null;
    }

    // 上报打印并获取最新的打印次数
    try {
        const latestPrintCount = await API.reportOrderPrint({
            orderViewId: orderUnifiedInfo.wmOrderViewId,
            wmPoiId: orderUnifiedInfo.wmPoiId,
            type: 1, // 标签打印
        });
        return latestPrintCount;
    } catch (e) {
        return null;
    }
};

/** 直接打印一段XML */
const printRawXml = (xml: string) => {
    if (!isInApp) {
        warningToast('请在PC客户端中进行此操作');
        return;
    }

    const poiEnabledConnectedPrinters = PrinterUtils.getPoiEnabledConnectedPrinters(
        Ids.getWmPoiId(),
    );
    // 未开启任何打印机，直接提示并退出流程
    if (poiEnabledConnectedPrinters.length === 0) {
        warningToast('当前尚未安装或选择有效的打印机');
        return;
    }

    poiEnabledConnectedPrinters.forEach(printerSetting => {
        const pageWidth = printerSetting.pageWidth || 0;
        const charWidth = PrinterUtils.charWidthOfPageWidth(pageWidth);

        if (window.wmPrinterApi) {
            window.wmPrinterApi.print(printerSetting.id, xml, { charWidth });
        }
    });
};

/** 打印退款提示 */
const printRefundTips = (orderSeqNum: number, endTime: string, wmOrderViewId: string) => {
    if (!isInApp) {
        return;
    }
    const xml = `
        <print>
            <div font="B" align="center">#${orderSeqNum}订单申请退款</div>
            <div font="H">请于 ${endTime} 前处理退款请求，超时未处理系统将自动退款</div>
            <div font="D">订单号：${wmOrderViewId}</div>
        </print>
    `;
    printRawXml(xml);
};

/** 金额退打印退款提示 */
const printAmountRefundTips = (orderSeqNum: number, wmOrderViewId: string) => {
    if (!isInApp) {
        return;
    }
    const xml = `
        <print>
            <div font="B" align="center">#${orderSeqNum}美团外卖订单申请退款</div>
            <div font="H">美团外卖用户申请退款，请您及时处理</div>
            <div font="D">订单号：${wmOrderViewId}</div>
        </print>
    `;
    printRawXml(xml);
};

/** 打印订单取消提示 */
const printOrderCancelTips = (orderSeqNum: number, wmOrderViewId: string, cancelReason: string) => {
    if (!isInApp) {
        return;
    }
    const xml = `
        <print>
            <div font="B" align="center">#${orderSeqNum}订单已取消</div>
            <div font="H">请勿继续备餐配送</div>
            <div font="D">订单号：${wmOrderViewId}</div>
            <div font="D">取消原因：${cancelReason}</div>
        </print>
    `;

    printRawXml(xml);
};

/** 打印订单部分退款提示 */
const printOrderPartialRefundTips = (
    orderSeqNum: number,
    wmOrderViewId: string,
    refundReason: string,
) => {
    if (!isInApp) {
        return;
    }
    const xml = `
        <print>
            <div font="B" align="center">#${orderSeqNum}订单已部分退款</div>
            <div font="D">${refundReason}</div>
            <div font="D">订单号：${wmOrderViewId}</div>
        </print>
    `;

    printRawXml(xml);
};

/** 负订单打印提示 */
const printOrderNegativeTips = (
    printTitle: string,
    wmOrderViewId: string,
    printContent: string,
) => {
    if (!isInApp) {
        return;
    }
    const xml = `
        <print>
            <div font="B" align="center">${printTitle}</div>
            <div font="H">${printContent}</div>
            <div font="D">订单号：${wmOrderViewId}</div>
        </print>
    `;

    printRawXml(xml);
};

export {
    printOrder,
    printOrderWhenConfirm,
    printOrderTag,
    printRefundTips,
    printOrderCancelTips,
    printAmountRefundTips,
    printOrderPartialRefundTips,
    printOrderNegativeTips,
    printRawTagData,
    printRawXml,
};
/* jscpd:ignore-end */
